/**
 * Cookie管理工具
 * 根据用户的Cookie偏好设置来控制Cookie的注入
 */

// 获取用户的Cookie偏好设置
export const getCookiePreferences = () => {
  try {
    const prefs = sessionStorage.getItem('cookiePreferences');
    if (prefs) {
      return JSON.parse(prefs);
    }
  } catch (e) {
    console.error('Failed to parse cookie preferences:', e);
  }
  
  // 默认所有Cookie都开启(包括性能、功能、定向)
  return {
    necessary: true,
    performance: true,
    functional: true,
    targeting: true,
  };
};

// 检查是否允许某类Cookie
export const isCookieAllowed = (type) => {
  const prefs = getCookiePreferences();
  return prefs[type] === true;
};

// 设置Cookie - 只有在允许的情况下才设置
export const setCookie = (name, value, options = {}) => {
  // 检查是否是必要的Cookie
  const isNecessary = options.category === 'necessary';
  
  // 如果不是必要的Cookie,检查用户是否同意
  if (!isNecessary && !isCookieAllowed(options.category || 'functional')) {
    console.log(`Cookie "${name}" blocked: user has not consented to ${options.category || 'functional'} cookies`);
    return false;
  }
  
  // 构建Cookie字符串
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  // 添加选项
  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += '; path=/';
  }
  
  if (options.expires) {
    if (typeof options.expires === 'number') {
      const date = new Date();
      date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    } else if (options.expires instanceof Date) {
      cookieString += `; expires=${options.expires.toUTCString()}`;
    }
  }
  
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  
  if (options.secure) {
    cookieString += '; Secure';
  }
  
  if (options.sameSite) {
    cookieString += `; SameSite=${options.sameSite}`;
  }
  
  // 设置Cookie
  document.cookie = cookieString;
  console.log(`Cookie "${name}" set successfully`);
  return true;
};

// 获取Cookie值
export const getCookie = (name) => {
  const nameEQ = encodeURIComponent(name) + '=';
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  
  return null;
};

// 删除Cookie
export const deleteCookie = (name, options = {}) => {
  setCookie(name, '', {
    ...options,
    expires: -1
  });
};

// 清除所有非必要的Cookie
export const clearNonEssentialCookies = () => {
  const cookies = document.cookie.split(';');
  
  cookies.forEach(cookie => {
    const cookieName = cookie.trim().split('=')[0];
    
    // 保留必要的Cookie,删除其他所有Cookie
    if (!isNecessaryCookie(cookieName)) {
      deleteCookie(cookieName, { path: '/' });
    }
  });
  
  console.log('Non-essential cookies cleared');
};

// 判断是否为必要的Cookie
const isNecessaryCookie = (cookieName) => {
  // 这里可以根据实际情况定义哪些Cookie是必要的
  const necessaryCookies = [
    'cookie_consent',
    'session_id',
    'csrf_token',
    // 添加其他必要的Cookie名称
  ];
  
  return necessaryCookies.some(name => cookieName.includes(name));
};

// 初始化Cookie管理器 - 在应用启动时调用
export const initCookieManager = () => {
  const prefs = getCookiePreferences();
  
  // 如果没有保存的偏好设置,说明用户还没有做出选择
  if (!sessionStorage.getItem('cookiePreferences')) {
    console.log('Cookie preferences not set. Waiting for user consent.');
    return;
  }
  
  // 如果用户拒绝了所有非必要Cookie,清除它们
  if (!prefs.performance && !prefs.functional && !prefs.targeting) {
    clearNonEssentialCookies();
  }
  
  console.log('Cookie manager initialized with preferences:', prefs);
};

// 导出默认的Cookie类别常量
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  PERFORMANCE: 'performance',
  FUNCTIONAL: 'functional',
  TARGETING: 'targeting',
};
